import angular from 'angular'

import { isText, toNumber } from './bits'
import { assign, identity, includes, isFinite } from 'lodash-es'

import getEnv from './get-env'
import StrictObj from './strict-obj'
import LockableObj from './lockable-obj'
import attachGetters from './attach-getters'

import {
  GALLERY_INCLUDE_ATTR,
  RANDOM_ORDER, RECENT_ORDER, POPULAR_ORDER,
  MODE_ATTR, HOSTED_MODE, IFRAME_MODE, IFRAMELESS_MODE
} from './shared-constants'

angular
  .module('ggs.utils')
  .factory('env', envProvider)

function envProvider($log, $rootElement) {

  // current mode
  const currentMode = getEnv({
    elm: $rootElement,
    def: IFRAMELESS_MODE,
    elmAttr: MODE_ATTR,
    msg: (val) => `${__filename} gallery will use ${val} as currentMode`,
    cast: identity,
    check: (val) => includes([HOSTED_MODE, IFRAME_MODE, IFRAMELESS_MODE], val),
    isTrustedLocation: false
  })

  const env = attachGetters(StrictObj({}))
  env.isHostedMode = HOSTED_MODE === currentMode
  env.isIframeMode = IFRAME_MODE === currentMode
  env.isIframelessMode = IFRAMELESS_MODE === currentMode

  // site related
  env.isTrustedLocation = false
    || env.isHostedMode
    || env.isIframeMode

  env.isEmbedInCommonSite = false
    || env.isIframeMode
    || env.isIframelessMode

  // isTrustedLocation based getters
  const getEnvGeneric = (conf) => getEnv(assign({
    elm: $rootElement,
    print: $log.info,
    isTrustedLocation: env.isTrustedLocation
  }, conf))

  const getEnvStr = (conf) => getEnvGeneric(assign({
    cast: identity,
    check: isText
  }, conf))

  const getEnvNum = (conf) => getEnvGeneric(assign({
    cast: toNumber,
    check: isFinite
  }, conf))

  const getEnvBool = getEnvGeneric

  // components settings
  env.requiresCssBodyReset = getEnvBool({
    def: false
      || env.isHostedMode
      || env.isIframeMode,
    key: [
      'require-css-body-reset',
      'requires-css-body-reset'
    ],
    msg: `${__filename} gallery will :not: inject styles.body-reset.raw.less`
  })

  env.requiresCssHardReset = getEnvBool({
    def: false,
    key: [
      'require-css-hard-reset',
      'requires-css-hard-reset'
    ],
    msg: (val) => val
      ? `${__filename} gallery will inject styles.gallery-hard-reset.raw.less - it slows browser up to 2x`
      : `${__filename} gallery will not inject styles.gallery-hard-reset.raw.less`
  })

  // props
  env.props = LockableObj(StrictObj({}))

  env.props.galleryKey = getEnvStr({
    def: 'unknown-key',
    elmAttr: GALLERY_INCLUDE_ATTR,
    urlPathRE: /\/g\/([^&?/]+)/,
    urlSearchKey: ['id', 'key']
  })

  env.props.strictOrder = getEnvBool({
    def: null,
    key: 'strict-order',
    msg: `${__filename} gallery will :not: use the strict order`
  })

  env.props.initOrderType = getEnvGeneric({
    def: null,
    key: 'sort',
    urlSearchKey: 'o',
    msg: (val) => `${__filename} gallery will use [initOrderType=${val}]`,
    cast: (v) => {
      if (v === 'r') return RECENT_ORDER
      if (v === 'p') return POPULAR_ORDER
      return v
    },
    check: (v) => includes([RANDOM_ORDER, RECENT_ORDER, POPULAR_ORDER], v)
  })

  env.props.sharedPostKey = getEnvStr({
    def: null,
    elmAttr: 'displayed-item',
    urlPathRE: /\/g\/[^&?/]+\/([^&?/]+)/,
    urlSearchKey: ['d', 'item_ref'],
    isTrustedLocation: true,
    msg: (val) => `${__filename} gallery will use [sharedPostKey=${val}]`
  })

  env.props.sharedSlideIndex = getEnvNum({
    def: null,
    elmAttr: 'displayed-slide',
    urlPathRE: /\/g\/[^&?/]+\/[^&?/]+\/([^&?/]+)/,
    urlSearchKey: 'i',
    isTrustedLocation: true,
    msg: (val) => `${__filename} gallery will use [sharedSlideIndex=${val}]`
  })

  env.props.locale = getEnvStr({
    def: null,
    key: 'locale',
    msg: (val) => `${__filename} gallery will override [locale=${val}]`
  })

  env.props.allowEntry = getEnvBool({
    def: null,
    key: 'enter',
    urlSearchKey: 'e',
    msg: `${__filename} gallery will :not: show enter button`
  })

  env.props.allowHeader = getEnvBool({
    def: null,
    key: 'header',
    urlSearchKey: 'h',
    msg: `${__filename} gallery will :not: show header`
  })

  env.props.allowSorting = getEnvBool({
    def: null,
    key: 'sorting',
    urlSearchKey: 's',
    msg: `${__filename} gallery will :not: show sorting button`
  })

  env.props.rootLocation = getEnvStr({
    def: null,
    key: 'location',
    urlSearchKey: 'l',
    msg: (val) => `${__filename} gallery will override [rootLocation=${val}]`
  })

  env.props.instantModeUntilReady = getEnvStr({
    def: null,
    key: 'instant-mode-until-ready',
    msg: `${__filename} gallery will :not: force the instant mode`
  })

  env.props.forceCarouselMode = getEnvBool({
    def: null,
    key: 'force-carousel-mode',
    msg: `${__filename} gallery will :not: force the carousel mode`
  })

  env.props.forceHostedShareLink = getEnvBool({
    def: null,
    key: 'force-hosted-share-link',
    msg: `${__filename} gallery will :not: force format hosted share urls`
  })

  env.props.forceEmbeddedShareLink = getEnvBool({
    def: null,
    key: 'force-embedded-share-link',
    msg: `${__filename} gallery will :not: force format sharing url in old embedded style`
  })

  env.props.forceTransparentBodyBackground = getEnvBool({
    def: null,
    key: 'force-transparent-body-background',
    msg: `${__filename} gallery will :not: force the body background to be transparent`
  })

  env.props.allowOverflowYChange = getEnvBool({
    def: null,
    key: 'allow-overflow-y-change',
    msg: `${__filename} gallery will :not: change html.style.overflowY`
  })

  env.props.modalsFocusableCloseButton = getEnvBool({
    def: null,
    key: 'modals-focusable-close-button',
    msg: `${__filename} gallery will :not: make the close buttons of modals focusable`
  })

  env.props.campaignFocusableCloseButton = getEnvBool({
    def: null,
    key: 'campaign-focusable-close-button',
    msg: `${__filename} gallery will :not: make the close button of its campaign focusable`
  })

  // export
  return env
}
